import { useEffect, useState, useRef, useCallback } from 'react';

/**
 * Hook per creare un valore debounced
 * Utile per ritardare aggiornamenti di stato o chiamate API
 * 
 * @param {T} value - Il valore da debounce
 * @param {number} delay - Ritardo in millisecondi
 * @returns {T} Il valore dopo il delay
 */
export function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    // Aggiorna il valore debounced dopo il delay
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    // Annulla il timer se value cambia (o se il componente viene smontato)
    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}

/**
 * Hook per creare una funzione debounced
 * Evita chiamate multiple ravvicinate a funzioni costose
 * 
 * @param {Function} fn - La funzione da debounce
 * @param {number} delay - Ritardo in millisecondi
 * @param {Array} dependencies - Dipendenze per ricreare la funzione
 * @returns {Function} La funzione debounced
 */
export function useDebouncedCallback<T extends (...args: any[]) => any>(
  fn: T,
  delay: number,
  dependencies: React.DependencyList = []
): ((...args: Parameters<T>) => void) & { flush: () => void } {
  const timeoutRef = useRef<number | null>(null);
  const pendingArgsRef = useRef<Parameters<T> | null>(null);
  const fnRef = useRef(fn);
  fnRef.current = fn;

  const flush = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (pendingArgsRef.current) {
      const args = pendingArgsRef.current;
      pendingArgsRef.current = null;
      fnRef.current(...args);
    }
  }, []);

  const debouncedFn = useCallback((...args: Parameters<T>) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    pendingArgsRef.current = args;

    timeoutRef.current = window.setTimeout(() => {
      pendingArgsRef.current = null;
      timeoutRef.current = null;
      fnRef.current(...args);
    }, delay);
  }, [delay, ...dependencies]);

  // Cleanup quando il componente viene smontato
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const result = useCallback((...args: Parameters<T>) => {
    debouncedFn(...args);
  }, [debouncedFn]) as ((...args: Parameters<T>) => void) & { flush: () => void };
  result.flush = flush;

  return result;
}